// NFC-e no fechamento da venda do PDV.
//
// Uso no seu PDV (tela de finalizar venda):
//   <EmitirNFCe
//     venda={{ id: venda.id, itens: [...], pagamentos: [{forma:"17", valor: 50}], troco: 0 }}
//     onConcluido={(nota) => ...}   // nota autorizada (ou null se o operador pular)
//   />
//
// Cada item da venda: { codigo, descricao, ncm, unidade, quantidade, valor_unitario, desconto?, gtin?, cest?, tributos? }
// Se o item não trouxer `tributos`, o componente pede a sugestão pelo NCM.
import React, { useState } from "react";
import { abrirArquivo, fiscal } from "../api";
import { Aviso, Botao, Campo, Entrada } from "../components/ui";
import { brl, documentoValido, mascaraDocumento, round2, soDigitos } from "../utils";
import { ResultadoEmissao } from "./NFeForm";

export default function EmitirNFCe({ venda, onConcluido, emitirPorPadrao = true, abrirDanfe = true }) {
  const [emitir, setEmitir] = useState(emitirPorPadrao);
  // CPF/CNPJ e e-mail do cliente escolhido na venda já vêm preenchidos (dá para apagar ou trocar)
  const [cpf, setCpf] = useState(soDigitos(venda.cliente_documento || ""));
  const [email, setEmail] = useState(venda.cliente_email || "");
  const [enviando, setEnviando] = useState(false);
  const [nota, setNota] = useState(null);
  const [erro, setErro] = useState(null);

  const total = round2(
    venda.itens.reduce((a, i) => a + Number(i.quantidade) * Number(i.valor_unitario) - Number(i.desconto || 0), 0),
  );
  const docOk = !cpf || documentoValido(cpf);

  const concluir = async () => {
    if (!emitir) return onConcluido?.(null);
    setEnviando(true);
    setErro(null);
    try {
      const cache = {};
      const itens = await Promise.all(
        venda.itens.map(async (i) => {
          if (i.tributos) return i;
          cache[i.ncm] = cache[i.ncm] || fiscal.sugerirTributos({ ncm: i.ncm, modelo: "65" });
          return { ...i, tributos: await cache[i.ncm] };
        }),
      );
      const corpo = {
        venda_id: venda.id ? String(venda.id) : null,
        itens,
        pagamentos: venda.pagamentos,
        troco: venda.troco || 0,
        presenca: venda.entrega ? 4 : 1,
        destinatario: {
          documento: soDigitos(cpf) || null,
          nome: venda.cliente_nome || null,
          email: email || null,
          indicador_ie: 9,
        },
        enviar_email: Boolean(email),
      };
      const r = nota && ["rejeitada", "erro"].includes(nota.status)
        ? await fiscal.reenviar(nota.id, "65", corpo)
        : await fiscal.emitirNfce(corpo);
      setNota(r);
      if (r.status === "autorizada") {
        if (abrirDanfe) await abrirArquivo(`/notas/${r.id}/pdf`).catch(() => {});
        onConcluido?.(r);
      }
    } catch (e) {
      setErro(e.message);
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div className="space-y-3 rounded-lg border bg-white p-4 border-gray-200">
      <label className="flex items-center gap-2 text-sm font-medium">
        <input type="checkbox" checked={emitir} onChange={(e) => setEmitir(e.target.checked)} className="h-4 w-4" />
        Emitir NFC-e desta venda ({brl(total)})
      </label>

      {emitir && (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Campo rotulo="CPF/CNPJ na nota (opcional)" erro={!docOk ? "Documento inválido" : undefined}>
            <Entrada value={mascaraDocumento(cpf)} onChange={(e) => setCpf(soDigitos(e.target.value))} inputMode="numeric" />
          </Campo>
          <Campo rotulo="E-mail para envio do XML (opcional)">
            <Entrada type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
          </Campo>
        </div>
      )}

      {erro && <Aviso tipo="erro">{erro}</Aviso>}
      {nota && <ResultadoEmissao nota={nota} />}

      <div className="flex justify-end gap-2">
        {nota?.status === "autorizada" ? (
          <Botao variante="secundario" onClick={() => abrirArquivo(`/notas/${nota.id}/pdf`)}>Reimprimir DANFE NFC-e</Botao>
        ) : (
          <>
            {nota && nota.status !== "autorizada" && (
              <Botao variante="secundario" onClick={() => onConcluido?.(nota)}>Finalizar sem nota</Botao>
            )}
            <Botao carregando={enviando} disabled={!docOk} onClick={concluir}>
              {emitir ? (nota ? "Tentar novamente" : "Finalizar e emitir NFC-e") : "Finalizar venda"}
            </Botao>
          </>
        )}
      </div>
    </div>
  );
}
